<script setup lang="ts">
/**
 * Flow chart editor for a deck (wp-admin) or, mounted via mountFlowEditor, for a course
 * level or macro. State lives in useFlowEditor; the panels below read it via inject.
 */
import FlowCanvasPanel from '../flow-editor/components/FlowCanvasPanel.vue'
import FlowEditorToolbar from '../flow-editor/components/FlowEditorToolbar.vue'
import FlowInspector from '../flow-editor/components/FlowInspector.vue'
import FlowPalettePanel from '../flow-editor/components/FlowPalettePanel.vue'
import FlowRightRail from '../flow-editor/components/FlowRightRail.vue'
import { useFlowEditor } from '../flow-editor/composables/useFlowEditor'
import type { FlowEditorProps } from '../flow-editor/model/types'

const props = withDefaults(defineProps<FlowEditorProps>(), { nodeMode: 'level' })

const { paletteCollapsed, inspectorCollapsed, rightRailCollapsed } = useFlowEditor(props).ui
</script>

<template>
  <section class="flow-page">
    <FlowEditorToolbar />

    <div
      class="flow-layout"
      :style="{
        gridTemplateColumns: `${inspectorCollapsed ? '26px' : 'clamp(240px, 22vw, 320px)'} minmax(0, 1fr) ${rightRailCollapsed ? '26px' : 'clamp(240px, 22vw, 320px)'}`,
      }"
    >
      <div
        class="flow-center-col"
        :style="{
          gridTemplateRows: paletteCollapsed ? 'minmax(0, 1fr) 36px' : 'minmax(0, 1fr) 18.5vh',
        }"
      >
        <FlowCanvasPanel />
        <FlowPalettePanel />
      </div>

      <FlowInspector />
      <FlowRightRail />
    </div>
  </section>
</template>

<style scoped>
.flow-page {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  min-height: 0;
}

.flow-layout {
  display: grid;
  grid-template-columns:
    clamp(240px, 22vw, 320px)
    minmax(0, 1fr)
    clamp(240px, 22vw, 320px);
  grid-template-rows: minmax(0, 1fr);
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  transition: grid-template-columns 0.25s ease;
}

.flow-canvas-wrap,
.flow-inspector,
.flow-right-rail,
.flow-center-col {
  min-height: 0;
}

.flow-center-col {
  grid-column: 2;
  grid-row: 1;
  display: grid;
  grid-template-rows: minmax(0, 1fr) 18.5vh;
  gap: 0.75rem;
  transition: grid-template-rows 0.25s ease;
}

.flow-canvas-wrap,
.flow-inspector {
  display: flex;
  flex-direction: column;
}

.flow-right-rail {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.flow-inspector {
  grid-column: 1;
  grid-row: 1;
}

.flow-right-rail {
  grid-column: 3;
  grid-row: 1;
}

/* Remove the gray header background in the flow editor panels */
.flow-page :deep(.panel__header) {
  background: transparent;
  border-bottom-color: transparent;
}

@media (max-width: 1024px) {
  .flow-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(420px, 1fr) auto;
  }

  .flow-inspector,
  .flow-center-col,
  .flow-right-rail {
    grid-column: 1;
    grid-row: auto;
  }

  .flow-center-col {
    grid-template-rows: minmax(420px, 1fr) auto;
  }
}
</style>
